import { useRef, useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from "react-router-dom";

import { CurrProfileContext } from '../App/App.tsx';
import { supabase } from '../supabaseClient';
import { JamPadEvent, JamPadProfile, UserType } from '../DataModels.ts';
import { UUID } from 'crypto';

import { Person, ArrowBack, Verified, PersonAdd, PersonRemove, Edit, Settings } from '@mui/icons-material';
import ProfileEventPage from './ProfileEventPage.tsx';
import EventItem from '../Events/EventItem.tsx';
import { Box, Button, Typography } from '@mui/material';
import EditProfile from './EditProfile.tsx';

export enum ProfileEventView { 
    Saved = 0,
    Upcoming = 1, 
    Past = 2, 
    Performing = 3,
    Hosting = 4,
}
export default function ProfilePage() {
    const initialized = useRef(false);
    const nav = useNavigate();
    let { username } = useParams();
    const { profile: currUserProfile, setProfile: setCurrUserProfile, updateProfile } = useContext(CurrProfileContext);

    const [eventView, setEventView] = useState<ProfileEventView>(ProfileEventView.Upcoming);
    const [fullEvent, setFullEvent] = useState<JamPadEvent | null>(null);
    const [profile, setProfile] = useState<JamPadProfile>();
    const [currUser, setCurrUser] = useState(false);

    const [savedEvents, setSavedEvents] = useState<Map<UUID, JamPadEvent>>(new Map());
    const [upcomingEvents, setUpcomingEvents] = useState<Map<UUID, JamPadEvent>>(new Map());
    const [pastEvents, setPastEvents] = useState<Map<UUID, JamPadEvent>>(new Map());

    async function loadProfile(params) {
        const {data, error } = await supabase
            .rpc("load_profile", params)
            .select().single<JamPadProfile>();
        if(error) {
            console.error(error.message);
        } else if(data && !profile) {
            setCurrUser(data.id === currUserProfile?.id);
            setProfile(data);
        }
    }

    function initProfilePage() {
        if(username) {
            loadProfile({ username_in: username });
        } else {
            // empty query params -> default to curr user
            setCurrUser(true);
            if(currUserProfile) {
                setProfile(currUserProfile);
            } else {
                updateProfile();
            }
        }
    }
    useEffect(() => {
        if(currUserProfile && currUser) {
            setProfile(currUserProfile);
        }
    }, [currUserProfile]);

    async function doFollowAction() {
        if(!profile) { return; }

        const curr = currUserProfile?.id;
        if(!curr) { 
            alert("Please log in to follow profiles.");
            return;
        }
        if(!profile.user_following) {
            await supabase.from("following")
                .insert({ follower_id: curr, followed_id: profile.id });
            setProfile({ ...profile, user_following: true });
            if(currUserProfile) {
                setCurrUserProfile({ ...currUserProfile, following: currUserProfile.following + 1 });
            }
        } else if(window.confirm("Unfollow @" + profile.username + "?")) {
            await supabase.from("following")
                .delete()
                .eq("follower_id", curr)
                .eq("followed_id", profile.id);
            setProfile({ ...profile, user_following: false });
            if(currUserProfile) {
                setCurrUserProfile({ ...currUserProfile, following: currUserProfile.following - 1 });
            }
        }
    }

    useEffect(()=> {
        if(!initialized.current || !profile) {
            initProfilePage();
            initialized.current = true
        }
    }, []);



    return <>
        <Box key='mainProfile' sx={{
            flex: '1', background: '#e0e3ff', 
            padding: { xs: '2vh 3vw 0', md: '3vh 4vw 0' },
            display: fullEvent === null ? 'flex' : 'none', 
            flexDirection: 'column', gap: '16px', alignItems: 'center',
        }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', 
                gap: { xs: '26px', md: '40px' }, 
                alignSelf: 'start', alignItems: 'center'
            }}>
                <div>
                    { profile?.pfp && profile.pfp !== "" ?
                        <img src={profile.pfp} alt="pfp" style={{ 
                            maxWidth: '22vw', maxHeight: '22vh', 
                            aspectRatio: '1', objectFit: 'cover', borderRadius: 200,
                            border: '3px solid #4797ed'
                        }} />
                        : <Person style={{ fontSize: '16vh', padding: '2vh', background: 'white', borderRadius: 100 }}/>
                    }
                </div>
                <div style={{ flex: '2 0 60%', display: 'flex', flexDirection: 'column',
                    justifyContent: 'space-between', gap: '10px'
                }}>
                    <Box sx={{ flex: '1', display: 'flex', 
                        flexWrap: { xs: 'nowrap', md: 'wrap' }, 
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'left', md: 'center' }
                    }}>
                        <Typography sx={{ fontSize: '26px', 
                            paddingRight: { xs: '0', md: '18px' },
                        }}>
                            <b>{ profile?.display_name ?? "---"}</b>
                        </Typography>
                        <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                            <Typography style={{ fontSize: '20px' }}>
                                <i>@{profile?.username ?? "err"}</i>
                            </Typography>
                            {(profile?.type ?? UserType.Guest) > 0 && 
                                <Verified sx={{ fontSize: '18px', paddingLeft: '6px', 
                                    color: profile?.type === UserType.Artist ? 
                                        'blue'
                                    : ( profile?.type === UserType.Admin ?
                                        'yellow' : 'green'
                                    ) 
                                }}/>
                            }
                        </div>
                    </Box>
                    <Box style={{ flex: '1', display: 'flex', gap: '24px', 
                            fontSize: '18px', }}>
                        <Typography style={{ fontSize: 'inherit' }}>
                            <b>{ profile?.followers ?? 0 } followers</b>
                        </Typography>
                        <Typography style={{ fontSize: 'inherit', color: 'gray' }}><b>|</b></Typography>
                        <Typography style={{ fontSize: 'inherit' }}>
                            <b>{ profile?.following ?? 0 } following</b>
                        </Typography>
                    </Box>
                    { profile?.bio && profile.bio !== "" &&
                        <Typography sx={{ display: { xs: 'none', md: 'flex' },
                            flex: '1', alignSelf: 'start', fontSize: '20px' 
                        }}>
                            { profile.bio }
                        </Typography>
                    }
                </div>
            </Box>

            {/* Move bio under pfp to give more width on smaller screens */}
            { profile?.bio && profile.bio !== "" &&
                <Typography sx={{ display: { xs: 'flex', md: 'none' },
                    flex: '1', alignSelf: 'start', fontSize: '20px' 
                }}>
                    { profile.bio }
                </Typography>
            }

            <div style={{ flex: '1', gap: '20px', textAlign: 'center', 
                display: 'flex', justifyContent: 'center', paddingBottom: '10px'
            }}>
                { !currUser && currUserProfile &&
                    <Button variant='contained' sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                        color={profile?.user_following ? 'secondary' : 'primary' }
                        onClick={() => { doFollowAction(); }}
                    >{ profile?.user_following ?
                        <><PersonRemove/><div>Unfollow</div></>
                    :
                        <><PersonAdd/><div>Follow</div></>
                    }</Button>
                }
                { currUser && <>
                    <EditProfile profile={profile} setProfile={(p) => {setProfile(p)}}/>

                    <div>
                        <Button variant='contained' color='secondary'
                            sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}
                            onClick={() => nav('/settings') }
                        >
                            <Settings/>
                            Settings
                        </Button>
                    </div>
                </>}
            </div>
            
            <Box sx={{ display: 'flex', flexDirection: 'row', width: "100%",
                gap: {xs: '12px', md: '20px' }, alignItems: 'center',
                background: 'white', justifyContent: 'center', 
                fontSize: { xs: '18px', md: '22px' }, padding: '4px 0'
             }}>
                { currUser && <>
                    <button onClick={() => { setEventView(ProfileEventView.Saved) }} style={{ 
                        background: 'none', fontSize: 'inherit', border: 'none', flex: '1',
                        color: eventView === ProfileEventView.Saved ? 'darkblue' : 'lightblue', 
                    }}>Saved Events</button>
                    <Box sx={{ fontSize: { xs: '32px', md: '40px' }, color: 'lightblue'  }}>|</Box>
                </>}

                <button onClick={() => { setEventView(ProfileEventView.Upcoming) }} style={{ 
                    background: 'none', fontSize: 'inherit', border: 'none', flex: '1',
                    color: eventView === ProfileEventView.Upcoming ? 'darkblue' : 'lightblue', 
                }}>Upcoming Events</button>

                <Box sx={{ fontSize: { xs: '32px', md: '40px' }, color: 'lightblue'  }}>|</Box>
                
                <button onClick={() => { setEventView(ProfileEventView.Past) }} style={{ 
                    background: 'none', fontSize: 'inherit', border: 'none', flex: '1',
                    color: eventView === ProfileEventView.Past ? 'darkblue' :'lightblue',
                }}>Past Events</button>
            </Box>

            { currUser &&
                <ProfileEventPage profileID={profile?.id} view={ProfileEventView.Saved} 
                    currView={eventView}
                    events={savedEvents} setEvents={setSavedEvents} 
                    seeFullEvent={(e) => setFullEvent(e)} />
            }
            { profile?.public_future ?
                <ProfileEventPage profileID={profile?.id} view={ProfileEventView.Upcoming}
                    currView={eventView} 
                    events={upcomingEvents} setEvents={setUpcomingEvents} 
                    seeFullEvent={(e) => setFullEvent(e)} />
                : <Typography style={{ 
                        display: profile && eventView === ProfileEventView.Upcoming ? 'flex' : 'none', 
                        flex: '100', fontWeight: 'bold', paddingTop: '20px'
                    }} variant='h6'>[ These events are private ]</Typography>
            }
            { profile?.public_past ?
                <ProfileEventPage profileID={profile?.id} view={ProfileEventView.Past} 
                    currView={eventView}
                    events={pastEvents} setEvents={setPastEvents} 
                    seeFullEvent={(e) => setFullEvent(e)} />
                : <Typography style={{ 
                        display: profile && eventView === ProfileEventView.Past ? 'flex' : 'none', 
                        flex: '100', fontWeight: 'bold', paddingTop: '20px'
                    }} variant='h6'>[ These events are private ]</Typography>
            }
            { !profile && 
                // just take up space until loaded
                <div style={{ flex: '100' }}></div>
            }
        </Box>

        { fullEvent !== null &&
            <div key='fullEventProfile' style={{ 
                overflow: 'hidden',
                background: 'darkgray', position: 'absolute', zIndex: '2',
                top: '70px', left: '0', width: '100vw', display: 'flex', flex: '1'
            }}>
                <div style={{ flex: '1', padding: '20px', zIndex: '1', overflow: 'hidden auto' }}>
                    <EventItem col={false} event={fullEvent} />
                </div>
                <div style={{ zIndex: '2', position: 'absolute', top: '10px', left: '10px'}}>
                    <Button onClick={() => setFullEvent(null)} sx={{
                        borderRadius: '32px', padding: '10px 0'
                    }} variant='contained'><ArrowBack/></Button>
                </div>
            </div>
        }
    </>;
}